import { useState, useEffect } from 'react';
import { Send, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { ComputedSlot } from '@vagusx/clients';
import type { SlotSearchParams } from '../types';
import { useSchedulingChat } from '../hooks/use-scheduling-chat';

interface SchedulingChatProps {
  initialContext: SlotSearchParams;
  onSwitchToForm: () => void;
  showBookButton: boolean;
  onBook: (slot: ComputedSlot) => void;
  onSlotsFound?: (slots: ComputedSlot[]) => void;
}

export function SchedulingChat({
  initialContext,
  onSwitchToForm,
  showBookButton,
  onBook,
  onSlotsFound,
}: SchedulingChatProps): React.ReactElement {
  const { messages, isLoading, sendMessage, latestSlots } = useSchedulingChat(initialContext);
  const [input, setInput] = useState('');

  // Watch latestSlots and push to parent when they change
  useEffect(() => {
    if (latestSlots.length > 0) {
      onSlotsFound?.(latestSlots);
    }
  }, [latestSlots]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = (): void => {
    if (!input.trim()) return;
    sendMessage(input);
    setInput('');
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 pb-3">
        <Button variant="ghost" size="sm" onClick={onSwitchToForm} className="text-xs">
          <ArrowLeft className="h-3 w-3 mr-1" />
          Back to Form
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto space-y-4 pb-4">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-lg px-4 py-2.5 text-sm ${
                msg.role === 'user'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted'
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-muted rounded-lg px-4 py-2.5 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Searching...</span>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2 pt-3 border-t">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Ask about availability..."
          disabled={isLoading}
          maxLength={500}
        />
        <Button onClick={handleSend} disabled={!input.trim() || isLoading} size="icon">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
